import React from 'react';
import { Spacing } from 'apps/sigma2-ui/src/components/ui/Spacing';
import { Button } from 'apps/sigma2-ui/src/components/ui/Button';
import { Table } from 'apps/sigma2-ui/src/components/ui/Table';
import { Switch } from 'apps/sigma2-ui/src/components/ui/Switch';
import { AllowedOpenAiModels } from 'libs/end-to-end/src/types/databases/data-sets/data-set-resume-rubric';
import { SelectBoxWithSearch } from 'apps/sigma2-ui/src/components/ui/SelectBoxWithSearch';
import { SelectiveFilterStringBasedOptionComponent } from 'apps/sigma2-ui/src/SelectiveFilteringOptionComponent';
import { SupplementalDataSetResumeGradingRubricV2 } from 'libs/end-to-end/src/types/databases/data-sets/supplemental-data-set-config';
import { SupplementalResumesDataSetConfig } from 'libs/end-to-end/src/types/databases/data-sets/supplemental-data-set-config';
import { UseRubricConstructsDataPayload } from 'apps/sigma2-ui/src/data-wizard/components/use-rubric-constructs-data';
import { UseCrudOperationsPayload } from 'apps/sigma2-ui/src/data-wizard/components/composite-dataset/recipes/use-crud-operations';
import { ReactNode } from 'react';

export const DataSetSupplementalResumeGradingV2ConfigurationComponent = ({
  config,
  setConfig,
  rubricConfigOps,
  useRubricConstructsDataPayload,
  setCurrentRubricConfigIndex,
  setShowConstructsModal,
}: {
  config: SupplementalResumesDataSetConfig;
  setConfig: (config: SupplementalResumesDataSetConfig) => void;
  rubricConfigOps: UseCrudOperationsPayload<SupplementalDataSetResumeGradingRubricV2>;
  useRubricConstructsDataPayload: UseRubricConstructsDataPayload;
  setCurrentRubricConfigIndex: (index: number | null) => void;
  setShowConstructsModal: (show: boolean) => void;
}) => {
  return (
    <Spacing size={'lg'} direction={'vertical'}>
      <SelectBoxWithSearch
        type={'single'}
        setSelected={(value) => {
          setConfig({
            ...config,
            openAiModel: value as AllowedOpenAiModels,
          });
        }}
        selected={config.openAiModel}
        list={AllowedOpenAiModels.options.map((model) => {
          return {
            optionLabel: model,
            optionValue: model,
          };
        })}
        label={'OpenAI Model'}
      />

      <Switch
        checked={config.enableCompositeResumeScoring ?? false}
        onChange={() => {
          setConfig({
            ...config,
            enableCompositeResumeScoring: !config.enableCompositeResumeScoring,
          });
        }}
      >
        Enable Composite Resume Scoring
      </Switch>

      <Switch
        checked={config.useV2Grading ?? false}
        onChange={() => {
          setConfig({
            ...config,
            useV2Grading: !config.useV2Grading,
          });
        }}
      >
        Use Resume Parser V2
      </Switch>

      <Button
        behavior={{
          type: 'onClick',
          onClick: () => {
            rubricConfigOps.onAdd({
              filter: {
                type: 'equals',
                value: '',
              },
              rubricConstructId: '',
            });
          },
        }}
        size={'small'}
        variant={'primary'}
        text={'Create'}
      />

      <Table<SupplementalDataSetResumeGradingRubricV2>
        items={config.rubricsByJobTitle ?? []}
        columnDefinitions={[
          {
            id: 'filter',
            header: () => 'Filter',
            cell: ({ item, rowIndex }) => (
              <SelectiveFilterStringBasedOptionComponent
                option={item.filter}
                setOption={(option) => {
                  rubricConfigOps.onEdit(rowIndex, {
                    filter: option,
                  });
                }}
              />
            ),
          },
          {
            id: 'rubric_item',
            header: () => 'Rubric Item',
            cell: ({ item, rowIndex }) => {
              const foundItem = useRubricConstructsDataPayload.constructs.find(
                (i) => i.id === item.rubricConstructId,
              );

              let itemIdItem: ReactNode;

              if (foundItem) {
                itemIdItem = <div>{foundItem.name}</div>;
              } else if (item.rubricConstructId === '') {
                itemIdItem = <div>{'Select a rubric item'}</div>;
              } else {
                itemIdItem = <div>{'Missing rubric item'}</div>;
              }

              return (
                <Spacing size={'md'} direction={'vertical'}>
                  {itemIdItem}
                  <Button
                    behavior={{
                      type: 'onClick',
                      onClick: () => {
                        useRubricConstructsDataPayload.singleSelectConfig?.setCurrentConstructId(
                          item.rubricConstructId,
                        );
                        setCurrentRubricConfigIndex(rowIndex);
                        setShowConstructsModal(true);
                      },
                    }}
                    size={'small'}
                    variant={'outline'}
                    text={'Select Item'}
                  />
                </Spacing>
              );
            },
          },
          {
            id: 'delete',
            header: () => <></>,
            cell: ({ rowIndex }) => (
              <Button
                behavior={{
                  type: 'onClick',
                  onClick: () => {
                    rubricConfigOps.onDelete(rowIndex);
                  },
                }}
                size={'small'}
                variant={'destructive'}
                text={'Delete'}
              />
            ),
          },
        ]}
      />
    </Spacing>
  );
};
